import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { getAdminById } from '../../redux/adminSlice';
import { tryingnavigate, setPendingNavigation, clearPendingNavigation } from '../../redux/blockingSlice';
import CommonLoader from '../../components/CommonLoader';
import ErrorPage from '../../components/ErrorPage';
import AdminProfileTab from '../../components/adminTabs/AdminProfileTab';
import AdminPasswordTab from '../../components/adminTabs/AdminPasswordTab';

const AdminProfile = () => {
    const { t } = useTranslation('adminProfile');
    const dispatch = useDispatch();
    const { admin, loading, error } = useSelector((state) => state.admin);
    const { resnavigate, pendingNavigation } = useSelector((state) => state.blocking);
    const location = useLocation();
    // When a super-admin opens another admin's profile the id is passed via
    // navigation state; otherwise this is the logged-in user's own profile.
    const managedId = location.state?.key || null;
    const adminId = managedId || localStorage.getItem('ID');
    const [activeTab, setActiveTab] = useState('profile');
    const [mobileTabOpen, setMobileTabOpen] = useState(false);

    // Fetch admin data
    useEffect(() => {
        if (adminId) {
            dispatch(getAdminById(adminId));
        }
    }, [dispatch, adminId]);

    // Close mobile dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (mobileTabOpen && !event.target.closest('[data-testid~="admin-profile-mobile-tabs-dropdown"]')) {
                setMobileTabOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [mobileTabOpen]);

    const handleTabClick = (e, key) => {
        if (resnavigate) {
            e.preventDefault();
            dispatch(setPendingNavigation({ kind: 'tab', value: key }));
            dispatch(tryingnavigate(true));
            return;
        }
        setActiveTab(key);
    };

    // Auto-apply the requested tab once the dirty tab releases the block.
    useEffect(() => {
        if (!resnavigate && pendingNavigation?.kind === 'tab' && pendingNavigation.value) {
            setActiveTab(pendingNavigation.value);
            dispatch(clearPendingNavigation());
        }
    }, [resnavigate, pendingNavigation, dispatch]);

    // Tabs setup. Both tabs always show. When a super-admin manages another
    // admin (managedId set), the Password tab switches to a force-reset variant
    // (no current password) via the `managed` prop below.
    const allTabs = [
        { key: 'profile', label: t('adminProfile.tabs.profile'), testId: 'admin-profile-tab-profile' },
        { key: 'password', label: t('adminProfile.tabs.password'), testId: 'admin-profile-tab-password' },
    ];

    // Heading: "My Profile" for the logged-in user's own profile; the managed
    // admin's name (fallback "Admin Profile") when a super-admin opens another
    // admin from the Admins list.
    const managedName = [admin?.name, admin?.surname].filter(Boolean).join(' ').trim();
    const headingText = managedId
        ? (managedName || t('adminProfile.manage_title', 'Admin Profile'))
        : t('adminProfile.title');

    // Handle server/network errors
    if ((error?.status >= 500 && error?.status <= 599) || error?.message === 'Network Error') {
        return <ErrorPage error={error} pageName={t('adminProfile.page_name', 'Admin Profile')} />;
    }

    return (
        <div data-testid="admin-profile-container" className="bg-white rounded-2xl p-4 shadow-xl border border-gray-100 sm:p-6 mx-auto">
            {/* Header */}
            <div data-testid="admin-profile-header" className="mb-6">
                <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent ml-2">
                    {headingText}
                </h1>
            </div>

            {/* Modern Navigation Tabs */}
            <div data-testid="admin-profile-tabs-container" className="mb-6">
                {/* Desktop Navigation Tabs */}
                <div data-testid="admin-profile-tabs" className="hidden w-fit sm:flex flex-wrap justify-start gap-2 bg-gradient-to-r from-gray-50 to-blue-50/30 p-1 rounded-2xl shadow border border-gray-200 relative">
                    {allTabs.map(({ key, label, testId }) => (
                        <button
                            key={key}
                            data-testid={testId}
                            onClick={(e) => handleTabClick(e, key)}
                            disabled={loading}
                            className={`group relative px-6 py-3 text-sm font-semibold transition-all duration-300 rounded-xl z-10
                                ${activeTab === key && !loading
                                    ? 'text-white'
                                    : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'
                                } ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                            {activeTab === key && !loading && (
                                <motion.div
                                    layoutId="activeAdminProfileTab"
                                    className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                            <span className="relative z-20">{label}</span>
                        </button>
                    ))}
                </div>

                {/* Mobile Navigation - Dropdown Style */}
                <div data-testid="admin-profile-mobile-tabs-dropdown" className="sm:hidden">
                    <div className="bg-gradient-to-r from-gray-50 to-blue-50/30 rounded-2xl shadow-lg border border-gray-200 p-2">
                        <div className="relative">
                            <button
                                data-testid="mobile-tab-selector"
                                className="w-full flex items-center justify-between px-4 py-3 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 text-gray-700 font-semibold cursor-pointer"
                                onClick={() => setMobileTabOpen(!mobileTabOpen)}
                            >
                                <span>{allTabs.find(tab => tab.key === activeTab)?.label}</span>
                                <svg
                                    className={`w-5 h-5 transition-transform duration-300 ${mobileTabOpen ? 'rotate-180' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            {mobileTabOpen && (
                                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
                                    {allTabs.map(({ key, label, testId }) => (
                                        <button
                                            key={key}
                                            data-testid={`mobile-${testId}`}
                                            onClick={(e) => {
                                                handleTabClick(e, key);
                                                setMobileTabOpen(false);
                                            }}
                                            className={`w-full text-left px-4 py-3 text-sm font-semibold transition-all duration-300 border-b border-gray-100 last:border-b-0
                                                ${activeTab === key && !loading
                                                    ? 'text-white bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A]'
                                                    : 'text-gray-600 hover:text-[#4ABDBD] hover:bg-gray-50'
                                                } ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Tab Content */}
                <div data-testid="admin-profile-content" className="p-4 pb-0">
                    {loading ? (
                        <div data-testid="admin-profile-loading" className="flex flex-col items-center justify-center py-4 pt-12">
                            <CommonLoader />
                            <span data-testid="admin-profile-loading-text" className="text-xl text-[#4ABDBD]">{t('adminProfile.loading')}</span>
                        </div>
                    ) : (
                        <>
                            {activeTab === 'profile' && (
                                <AdminProfileTab admin={admin} id="admin-profile-tab-content-profile" />
                            )}
                            {activeTab === 'password' && (
                                <AdminPasswordTab admin={admin} managed={!!managedId} id="admin-profile-tab-content-password" />
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminProfile;